import { FieldType } from '@databases/model/field';
import {
  type MutableSelectValue,
  type SelectValue,
  type ValueJSON
} from '@databases/model/value';
import * as Y from 'yjs';

import { type DisposableElement, type WrappedYType } from '../helpers/types.ts';
import { createUniqueStringArray } from '../helpers/utils.ts';
import { type YUniqueArray } from '../helpers/y-unique-array.ts';
import { ValueProperties } from '../y-entry.ts';
import { FieldMapping } from '../y-field.ts';

export class YSelectValue
  implements SelectValue, MutableSelectValue, WrappedYType, DisposableElement
{
  private uniqueStringArray: YUniqueArray<string> | undefined;

  constructor(private input: Y.Map<any>) {
    input.observe(this.onValueChanged);

    const ref = input.get(ValueProperties.value);
    if (ref instanceof Y.Array) {
      this.uniqueStringArray = createUniqueStringArray(ref);
    }
  }

  private onValueChanged = (e: Y.YMapEvent<any>) => {
    if (e.keysChanged.has(ValueProperties.value)) {
      const val = this.input.get(ValueProperties.value);
      if (val instanceof Y.Array) {
        if (this.uniqueStringArray) {
          this.uniqueStringArray.dispose();
        }

        this.uniqueStringArray = createUniqueStringArray(val);
      } else if (this.uniqueStringArray) {
        this.uniqueStringArray.dispose();
        this.uniqueStringArray = undefined;
      }
      if (val instanceof Y.Array) {
        if (this.uniqueStringArray) {
          this.uniqueStringArray.dispose();
        }

        this.uniqueStringArray = createUniqueStringArray(val);
      } else if (this.uniqueStringArray) {
        this.uniqueStringArray.dispose();
        this.uniqueStringArray = undefined;
      }
    }
  };

  get value(): string[] {
    if (!this.uniqueStringArray) {
      return [];
    }

    return this.uniqueStringArray.toArray().flatMap((v) => {
      if (typeof v === 'string') {
        return [v];
      } else {
        return [];
      }
    });
  }

  get isEmpty() {
    return !this.uniqueStringArray?.length;
  }

  get type(): FieldType.Select {
    return FieldType.Select;
  }

  setTo(value: string[]): void {
    const valueRef = new Y.Array();
    this.input.set(ValueProperties.value, valueRef);
    this.uniqueStringArray = createUniqueStringArray(valueRef);
    for (const val of value) {
      this.uniqueStringArray.push(val);
    }
  }

  add(value: string): void {
    if (!this.uniqueStringArray) {
      const valueRef = new Y.Array();
      this.input.set(ValueProperties.value, valueRef);
      this.uniqueStringArray = createUniqueStringArray(valueRef);
    }

    this.uniqueStringArray.addEnd(value);
  }

  delete(values: string[]): void {
    if (!this.uniqueStringArray) {
      const valueRef = new Y.Array();
      this.input.set(ValueProperties.value, valueRef);
      this.uniqueStringArray = createUniqueStringArray(valueRef);
    }

    this.uniqueStringArray.delete(...values);
  }

  toJSON(): ValueJSON<FieldType.Select, string[]> {
    return {
      type: this.type,
      value: this.value
    };
  }

  static fromJSON(json: ValueJSON<FieldType.Select, string[]>) {
    return new YSelectValue(
      new Y.Map([
        [ValueProperties.type, FieldMapping[json.type]],
        [ValueProperties.value, Y.Array.from(json.value)]
      ])
    );
  }

  unwrap(): Y.AbstractType<any> {
    return this.input;
  }

  dispose(): void {
    this.input.unobserve(this.onValueChanged);

    if (this.uniqueStringArray) {
      this.uniqueStringArray.dispose();
    }
  }
}
